import { TemporaryPanel } from "../../../../teamsite/src/pages/NewAiDesign/types";


const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // Essential for drawing onto canvas if images are from different origins
    img.onload = () => resolve(img);
    img.onerror = (err) => {
      console.error(`Failed to load image: ${url}`, err);
      reject(new Error(`Failed to load image: ${url}`));
    };
    img.src = url;
  });
};
/**
 * Generates a composited panel data URL by drawing a base image and logos onto a canvas.
 * @param designPanel - The design panel object containing base image URL and logo data.
 * @returns Promise<string> - Resolves with the data URL (e.g., 'data:image/png;base64,...') or rejects on error.
 */
const PLACEHOLDER_IMAGE = '/images/placeholder.png'; // Path to your placeholder image

/**
 * Generates a composited panel data URL by drawing a base image and logos onto a canvas.
 * @param temporaryPanel - The design panel object containing base image URL and logo data.
 * @returns Promise<string> - Resolves with the data URL (e.g., 'data:image/png;base64,...') or rejects on error.
 */
export const generateCompositedPanelDataUrl = async (
  temporaryPanel: TemporaryPanel,
): Promise<string> => {


  const baseImageUrl = temporaryPanel?.images_data?.webp || temporaryPanel?.images_data?.src || PLACEHOLDER_IMAGE;

  let baseImage: HTMLImageElement;
  const partKey = temporaryPanel.partkey; // For logging

  // 1. Load Base Image
  try {
    baseImage = await loadImage(baseImageUrl);
  } catch (error) {
    console.error(`Compositing(${partKey}): Error loading base image: ${baseImageUrl}`, error);
    try {
      baseImage = await loadImage(PLACEHOLDER_IMAGE);
      console.warn(`Compositing(${partKey}): Using placeholder image.`);
    } catch (placeholderError) {
      console.error(`Compositing(${partKey}): FATAL: Could not load base or placeholder image.`, placeholderError);
      throw new Error(`Failed to load base image and placeholder for ${partKey}`);
    }
  }

  // 2. Calculate Target Canvas Dimensions
  const naturalWidth = baseImage.naturalWidth;
  const naturalHeight = baseImage.naturalHeight;

  if (naturalWidth === 0 || naturalHeight === 0) {
    console.error(`Compositing(${partKey}): Base image has zero dimensions.`);
    throw new Error(`Base image for ${partKey} has zero dimensions.`);
  }


  // 3. Load Logo Images (concurrently)
  const logoLoadPromises = temporaryPanel.images.map(logoData => {
    if (!logoData.images_data?.src && !logoData.images_data?.webp) {
      console.warn(`Compositing(${partKey}): Logo ID ${logoData.id} has no image_data URL. Skipping.`);
      return Promise.resolve({ ...logoData, logoImage: null, status: 'skipped' });
    }
    return loadImage(logoData.images_data.webp || logoData.images_data.src)
      .then(logoImage => ({ ...logoData, logoImage, status: 'fulfilled' }))
      .catch(error => {
        console.error(`Compositing(${partKey}): Failed to load logo image ID ${logoData.id}: ${logoData.images_data.webp || logoData.images_data.src}`, error);
        return { ...logoData, logoImage: null, status: 'rejected' };
      });
  });

  const loadedLogosResults = await Promise.all(logoLoadPromises);
  const successfullyLoadedLogos = loadedLogosResults.filter(result => result.status === 'fulfilled' && result.logoImage);


  // 4. Create Target Offscreen Canvas and Draw
  const canvas = document.createElement('canvas');
  canvas.width = naturalWidth;
  canvas.height = naturalHeight;
  const ctx = canvas.getContext('2d');
  //(ctx: CanvasRenderingContext2D | null, boxes: TextBoxInput[] = [], options: Record<string, unknown> = {})
  if (!ctx) {
    throw new Error(`Could not get 2D context for offscreen canvas for part ${partKey}`);
  }

  if (!ctx) {
    throw new Error(`Could not get 2D context for offscreen canvas for part ${partKey}`);
  }

  // --- Drawing ---
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Draw Scaled Base Image
  try {
    ctx.drawImage(baseImage, 0, 0, naturalWidth, naturalHeight);
  } catch (e) {
    console.error(`Compositing(${partKey}): Error drawing base image onto canvas`, e);
    ctx.fillStyle = '#cccccc'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#555555'; ctx.font = '16px Arial'; ctx.textAlign = 'center';
    ctx.fillText('Base Panel Error', canvas.width / 2, canvas.height / 2);
  }


  // Draw Successfully Loaded Logos (Scaled)
  successfullyLoadedLogos.forEach(pLogo => {
    if (!pLogo.logoImage) return; // Should not happen due to filter, but check anyway

    const { x_position, y_position, scale, rotation, logoImage, id } = pLogo;
    const baseWidth = logoImage.naturalWidth;
    const baseHeight = logoImage.naturalHeight;

    // Apply scale before positioning so top-left stays anchored correctly
    const imgWidth = scale ? baseWidth * scale : baseWidth;
    const imgHeight = scale ? baseHeight * scale : baseHeight;

    const x = (x_position || 0) + (imgWidth / 2);
    const y = (y_position || 0) + (imgHeight / 2);
    ctx.save(); // Save context state before transforming for this logo
    ctx.translate(x, y); // Move origin to logo's center
    if (rotation) {
      ctx.rotate(rotation * Math.PI / 180); // Apply rotation
    }


    // Draw the actual logo image if loaded successfully
    try {
      // Draw image centered around the translated origin (x, y)
      ctx.drawImage(logoImage, -imgWidth / 2, -imgHeight / 2, imgWidth, imgHeight);
    } catch (e) {
      console.error(`Error drawing logo image ${id}:`, e);
      // Draw fallback square on drawing error (e.g., tainted canvas after load)
      ctx.fillStyle = 'rgba(255, 0, 0, 0.5)'; ctx.fillRect(-20, -20, 40, 40);
    }
    ctx.restore(); // Restore context state
  });



  // 5. Generate and Return Data URL
  try {
    const dataUrl = canvas.toDataURL('image/png'); // Or 'image/jpeg'
    return dataUrl;
  } catch (e) {
    console.error(`Compositing(${partKey}): Error generating data URL`, e);
    throw new Error(`Could not generate data URL for ${partKey}`);
  }
};


